import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Menu, X, Mountain, Snowflake, Wine, Waves, Sun, Anchor, 
  Beer, Fish, MapPin, Calendar, Users, Mail, Phone, Send,
  ChevronRight, Star, Instagram, Facebook, Twitter, ArrowRight, Bus
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URL from user's assets
const LOGO_URL = "https://customer-assets.emergentagent.com/job_outdoor-voyage/artifacts/j2pfbd0n_waypoint%20group%20clean%20logo%20with%20name.png";

// Activity Icons mapping
const activityIcons = {
  "Hiking": Mountain,
  "Winter Sports": Snowflake,
  "Wine & Spirits": Wine,
  "Water Sports": Waves,
  "Summer Fun": Sun,
  "Sailing": Anchor,
  "Fishing": Fish,
  "Group Travel": Bus,
};

// Navigation Component
const Navigation = ({ isScrolled }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { name: "Adventures", href: "#activities" },
    { name: "Upcoming Trips", href: "#trips" },
    { name: "About", href: "#about" },
    { name: "Contact", href: "#contact" },
  ];

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
          isScrolled ? "glass-dark" : "bg-transparent"
        }`}
        data-testid="main-navigation"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a href="#hero" className="flex items-center" data-testid="nav-logo">
              <img 
                src={LOGO_URL} 
                alt="Waypoint Group" 
                className="h-12 md:h-14 w-auto"
              />
            </a>

            {/* Desktop Nav Links */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="font-heading text-sm font-semibold tracking-wider uppercase text-white/80 hover:text-secondary transition-colors"
                  data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  {link.name}
                </a>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden md:block">
              <a href="#contact">
                <Button 
                  className="btn-primary"
                  data-testid="nav-cta-button"
                >
                  Book a Trip
                </Button>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-white p-2"
              onClick={() => setMobileMenuOpen(true)}
              data-testid="mobile-menu-button"
            >
              <Menu size={28} />
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-navy z-[100] flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="flex justify-between items-center p-6">
              <img src={LOGO_URL} alt="Waypoint Group" className="h-10" />
              <button 
                onClick={() => setMobileMenuOpen(false)}
                className="text-white p-2"
                data-testid="mobile-menu-close"
              >
                <X size={28} />
              </button>
            </div>
            <div className="flex-1 flex flex-col justify-center items-center gap-8">
              {navLinks.map((link) => (
                <motion.a
                  key={link.name}
                  href={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="font-heading text-4xl font-bold uppercase text-white hover:text-secondary transition-colors"
                  whileHover={{ x: 10 }}
                >
                  {link.name}
                </motion.a>
              ))}
              <a href="#contact" onClick={() => setMobileMenuOpen(false)}>
                <Button className="btn-primary mt-8">Book a Trip</Button>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      id="hero" 
      className="hero-section"
      style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1767843151979-bd1143463988?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxoaWtpbmclMjBncm91cCUyMG1vdW50YWluJTIwdmlld3xlbnwwfHx8fDE3Njk3MzczODN8MA&ixlib=rb-4.1.0&q=85')`
      }}
      data-testid="hero-section"
    >
      <div className="hero-overlay" />
      <div className="noise-overlay" />
      
      <div className="relative z-10 min-h-screen flex flex-col justify-end pb-20 md:pb-32 px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-4xl"
        >
          <span className="font-heading text-secondary text-sm md:text-base font-bold tracking-[0.3em] uppercase mb-4 block">
            NYC & North Jersey
          </span>
          <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black text-white tracking-tighter uppercase leading-none mb-6">
            Escape the City.<br />
            <span className="text-secondary">Find Adventure.</span>
          </h1>
          <p className="font-body text-lg md:text-xl text-white/80 max-w-xl mb-10 leading-relaxed">
            From mountain peaks to vineyard valleys, beach days to boat parties — 
            we take you from urban jungle to outdoor adventure.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a href="#activities">
              <Button className="btn-primary flex items-center gap-2" data-testid="hero-explore-btn">
                Explore Adventures <ChevronRight size={20} />
              </Button>
            </a>
            <a href="#trips">
              <Button className="btn-outline flex items-center gap-2" data-testid="hero-trips-btn">
                View Upcoming Trips
              </Button>
            </a>
          </div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
          className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2"
        >
          <div className="w-1.5 h-3 bg-secondary rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  );
};

// Activities Section with Bento Grid
const ActivitiesSection = ({ activities }) => {
  return (
    <section id="activities" className="section-padding bg-muted" data-testid="activities-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12 md:mb-16"
        >
          <span className="font-heading text-secondary text-sm font-bold tracking-[0.3em] uppercase mb-4 block">
            What We Do
          </span>
          <h2 className="font-heading text-4xl md:text-6xl font-bold text-navy tracking-tight uppercase">
            Your Next Adventure<br />Awaits
          </h2>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6">
          {activities.map((activity, index) => {
            const Icon = activityIcons[activity.category] || Mountain;
            const isLarge = index === 0;
            const isWide = index === 2;
            
            return (
              <motion.div
                key={activity.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8 }}
                className={`activity-card cursor-pointer rounded-sm overflow-hidden ${
                  isLarge ? "md:col-span-2 md:row-span-2" : ""
                } ${isWide ? "md:col-span-2" : ""}`}
                data-testid={`activity-card-${activity.id}`}
              >
                <div className={`relative ${isLarge ? "h-[400px] md:h-full" : "h-[300px]"}`}>
                  <img
                    src={activity.image_url}
                    alt={activity.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-secondary rounded-sm flex items-center justify-center">
                        <Icon size={20} className="text-navy" />
                      </div>
                      <span className="font-heading text-xs font-bold tracking-wider uppercase text-secondary">
                        {activity.category}
                      </span>
                    </div>
                    <h3 className={`font-heading font-bold text-white tracking-tight uppercase ${
                      isLarge ? "text-3xl md:text-4xl" : "text-2xl"
                    }`}>
                      {activity.name}
                    </h3>
                    <p className="font-body text-white/70 mt-2 text-sm md:text-base line-clamp-2">
                      {activity.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* View All Button */}
        <div className="mt-12 text-center">
          <a href="#trips">
            <Button 
              variant="outline" 
              className="border-2 border-navy text-navy hover:bg-navy hover:text-white font-heading font-bold uppercase tracking-wider px-8 py-6"
              data-testid="view-all-activities-btn"
            >
              Browse Trip Ideas <ArrowRight className="ml-2" size={18} />
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

// Trip Request Modal
const TripRequestModal = ({ isOpen, onClose, selectedTrip }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    group_size: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        message: `Trip Request: ${selectedTrip?.title || 'General Inquiry'}`,
        trip_type: selectedTrip?.category || 'custom',
      });
      toast.success("Request submitted! We'll get back to you within 24 hours.");
      setFormData({ name: "", email: "", phone: "", group_size: "" });
      onClose();
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-navy/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="trip-request-modal-overlay"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white rounded-sm p-6 md:p-8 max-w-md w-full shadow-xl"
          onClick={(e) => e.stopPropagation()}
          data-testid="trip-request-modal"
        >
          <div className="flex justify-between items-start mb-6">
            <div>
              <span className="font-heading text-secondary text-xs font-bold tracking-[0.2em] uppercase block mb-1">
                Request Trip
              </span>
              <h3 className="font-heading text-2xl font-bold text-navy uppercase">
                {selectedTrip?.title || "Custom Adventure"}
              </h3>
            </div>
            <button 
              onClick={onClose}
              className="text-muted-foreground hover:text-navy transition-colors"
              data-testid="trip-request-modal-close"
            >
              <X size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="font-heading text-xs font-bold text-navy mb-2 block uppercase tracking-wider">
                Name *
              </label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Your name"
                className="border-gray-200 h-11"
                data-testid="trip-request-name"
                required
              />
            </div>
            <div>
              <label className="font-heading text-xs font-bold text-navy mb-2 block uppercase tracking-wider">
                Email *
              </label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="your@email.com"
                className="border-gray-200 h-11"
                data-testid="trip-request-email"
                required
              />
            </div>
            <div>
              <label className="font-heading text-xs font-bold text-navy mb-2 block uppercase tracking-wider">
                Phone (Optional)
              </label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="(555) 555-5555"
                className="border-gray-200 h-11"
                data-testid="trip-request-phone"
              />
            </div>
            <div>
              <label className="font-heading text-xs font-bold text-navy mb-2 block uppercase tracking-wider">
                Group Size *
              </label>
              <Select 
                value={formData.group_size} 
                onValueChange={(value) => setFormData({ ...formData, group_size: value })}
              >
                <SelectTrigger className="border-gray-200 h-11" data-testid="trip-request-group-size">
                  <SelectValue placeholder="How many people?" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1-5">1-5 people</SelectItem>
                  <SelectItem value="6-15">6-15 people</SelectItem>
                  <SelectItem value="16-30">16-30 people</SelectItem>
                  <SelectItem value="30+">30+ people</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button 
              type="submit" 
              disabled={loading || !formData.group_size}
              className="w-full btn-primary h-12 mt-6"
              data-testid="trip-request-submit"
            >
              {loading ? "Submitting..." : "Submit Request"}
            </Button>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Trip Ideas Section (formerly Upcoming Trips)
const TripIdeasSection = ({ trips, onRequestTrip }) => {
  return (
    <section id="trips" className="section-padding bg-white" data-testid="trips-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12 md:mb-16"
        >
          <span className="font-heading text-secondary text-sm font-bold tracking-[0.3em] uppercase mb-4 block">
            Get Inspired
          </span>
          <h2 className="font-heading text-4xl md:text-6xl font-bold text-navy tracking-tight uppercase">
            Request Your<br />Next Adventure
          </h2>
          <p className="font-body text-muted-foreground text-lg mt-4 max-w-2xl">
            Browse our popular trip ideas and request your own custom adventure. 
            We'll handle all the details — you just show up ready for fun.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {trips.map((trip, index) => (
            <motion.div
              key={trip.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -8 }}
              className="group bg-white border border-gray-100 rounded-sm overflow-hidden shadow-sm hover:shadow-lg transition-shadow"
              data-testid={`trip-card-${trip.id}`}
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={trip.image_url}
                  alt={trip.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute top-4 left-4 bg-navy/80 backdrop-blur-sm text-white px-3 py-1 rounded-sm">
                  <span className="text-xs font-bold uppercase tracking-wider">{trip.category}</span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-heading text-xl font-bold text-navy uppercase">
                  {trip.title}
                </h3>
                <div className="flex items-center gap-2 mt-3 text-muted-foreground">
                  <MapPin size={14} />
                  <span className="text-sm">{trip.location}</span>
                </div>
                <p className="font-body text-muted-foreground text-sm mt-2">
                  Starting from <span className="font-bold text-navy">{trip.price}</span>/person
                </p>
                <Button 
                  size="sm" 
                  onClick={() => onRequestTrip(trip)}
                  className="w-full mt-4 bg-secondary text-navy hover:bg-secondary/90 font-heading uppercase tracking-wider"
                  data-testid={`request-trip-btn-${trip.id}`}
                >
                  Request Trip
                </Button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Custom Trip CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <p className="font-body text-muted-foreground mb-4">
            Don't see what you're looking for?
          </p>
          <Button 
            onClick={() => onRequestTrip({ title: "Custom Adventure", category: "custom" })}
            variant="outline" 
            className="border-2 border-navy text-navy hover:bg-navy hover:text-white font-heading font-bold uppercase tracking-wider px-8 py-6"
            data-testid="request-custom-trip-btn"
          >
            Request a Custom Trip <ArrowRight className="ml-2" size={18} />
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = ({ testimonials }) => {
  return (
    <section className="section-padding bg-navy relative overflow-hidden" data-testid="testimonials-section">
      <div className="noise-overlay" />
      
      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12 md:mb-16 text-center"
        >
          <span className="font-heading text-secondary text-sm font-bold tracking-[0.3em] uppercase mb-4 block">
            Testimonials
          </span>
          <h2 className="font-heading text-4xl md:text-6xl font-bold text-white tracking-tight uppercase">
            What Adventurers Say
          </h2>
        </motion.div>

        {/* Testimonial Marquee */}
        <div className="relative overflow-hidden py-4">
          <div className="testimonial-track">
            {[...testimonials, ...testimonials].map((testimonial, index) => (
              <div
                key={`${testimonial.id}-${index}`}
                className="flex-shrink-0 w-[350px] md:w-[400px] mx-3"
              >
                <div className="glass-effect rounded-sm p-6 h-full">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={16} className="fill-secondary text-secondary" />
                    ))}
                  </div>
                  <p className="font-body text-white/90 mb-6 leading-relaxed">
                    "{testimonial.text}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center">
                      <span className="font-heading font-bold text-navy">
                        {testimonial.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="font-heading font-bold text-white text-sm">
                        {testimonial.name}
                      </p>
                      <p className="text-white/60 text-xs">{testimonial.location}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const stats = [
    { number: "5,000+", label: "Adventurers" },
    { number: "200+", label: "Trips Per Year" },
    { number: "50+", label: "Destinations" },
    { number: "4.9", label: "Average Rating" },
  ];

  return (
    <section id="about" className="section-padding bg-white" data-testid="about-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="font-heading text-secondary text-sm font-bold tracking-[0.3em] uppercase mb-4 block">
              About Us
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-navy tracking-tight uppercase mb-6">
              Your Gateway to<br />the Outdoors
            </h2>
            <p className="font-body text-muted-foreground text-lg leading-relaxed mb-6">
              The Waypoint Group was founded with one mission: to make outdoor adventures 
              accessible to everyone in the NYC and North Jersey area. We believe that 
              escaping the urban grind shouldn't require a car, extensive planning, or 
              outdoor expertise.
            </p>
            <p className="font-body text-muted-foreground text-lg leading-relaxed mb-8">
              Whether you're a solo adventurer, a group of friends, or a corporate team 
              looking for something different, we've got you covered. From snow-capped 
              mountains to sun-soaked beaches, rolling vineyards to rushing rapids — 
              your next great story starts here.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center md:text-left"
                >
                  <p className="font-heading text-3xl md:text-4xl font-black text-navy">
                    {stat.number}
                  </p>
                  <p className="font-body text-sm text-muted-foreground mt-1">
                    {stat.label}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative h-[500px] md:h-[600px] rounded-sm overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1637092853885-d0f6fdefc166?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxuZXclMjB5b3JrJTIwY2l0eSUyMHNreWxpbmUlMjBzdW5zZXR8ZW58MHx8fHwxNzY5NzM3Mzg2fDA&ixlib=rb-4.1.0&q=85"
                alt="NYC Skyline"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/80 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <p className="font-accent text-4xl text-secondary">
                  From city streets to mountain peaks
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Newsletter Section
const NewsletterSection = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success("You're in! Check your inbox for adventure updates.");
      setEmail("");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("This email is already subscribed!");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="section-padding bg-secondary" data-testid="newsletter-section">
      <div className="max-w-4xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-navy tracking-tight uppercase mb-4">
            Never Miss an Adventure
          </h2>
          <p className="font-body text-navy/70 text-lg mb-8 max-w-xl mx-auto">
            Get first dibs on new trips, exclusive deals, and adventure inspiration 
            delivered straight to your inbox.
          </p>
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-white border-0 text-navy placeholder:text-navy/50 h-14 px-6 flex-1"
              data-testid="newsletter-email-input"
              required
            />
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-navy text-white hover:bg-navy-dark h-14 px-8 font-heading font-bold uppercase tracking-wider"
              data-testid="newsletter-submit-btn"
            >
              {loading ? "..." : "Subscribe"}
            </Button>
          </form>
          <p className="font-body text-navy/50 text-sm mt-4">
            No spam, ever. Unsubscribe anytime.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    trip_type: "",
    group_size: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent! We'll get back to you within 24 hours.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
        trip_type: "",
        group_size: "",
      });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="section-padding bg-muted" data-testid="contact-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="font-heading text-secondary text-sm font-bold tracking-[0.3em] uppercase mb-4 block">
              Get In Touch
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-navy tracking-tight uppercase mb-6">
              Plan Your<br />Adventure
            </h2>
            <p className="font-body text-muted-foreground text-lg leading-relaxed mb-8">
              Whether you're planning a solo expedition, a birthday celebration, 
              or a corporate retreat, we'd love to hear from you. Fill out the form 
              and our team will craft the perfect adventure.
            </p>
            
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-navy rounded-sm flex items-center justify-center">
                  <Mail className="text-secondary" size={20} />
                </div>
                <div>
                  <p className="font-heading font-bold text-navy">Email Us</p>
                  <p className="text-muted-foreground">Sead@WaypointNE.com</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-navy rounded-sm flex items-center justify-center">
                  <Phone className="text-secondary" size={20} />
                </div>
                <div>
                  <p className="font-heading font-bold text-navy">Call Us</p>
                  <p className="text-muted-foreground">(917) 588-0934</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-navy rounded-sm flex items-center justify-center">
                  <MapPin className="text-secondary" size={20} />
                </div>
                <div>
                  <p className="font-heading font-bold text-navy">Location</p>
                  <p className="text-muted-foreground">NYC & North Jersey</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-sm shadow-sm space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="font-heading text-sm font-bold text-navy mb-2 block uppercase tracking-wider">
                    Name
                  </label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your name"
                    className="border-gray-200 h-12"
                    data-testid="contact-name-input"
                    required
                  />
                </div>
                <div>
                  <label className="font-heading text-sm font-bold text-navy mb-2 block uppercase tracking-wider">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                    className="border-gray-200 h-12"
                    data-testid="contact-email-input"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="font-heading text-sm font-bold text-navy mb-2 block uppercase tracking-wider">
                    Phone (Optional)
                  </label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(555) 555-5555"
                    className="border-gray-200 h-12"
                    data-testid="contact-phone-input"
                  />
                </div>
                <div>
                  <label className="font-heading text-sm font-bold text-navy mb-2 block uppercase tracking-wider">
                    Group Size
                  </label>
                  <Select 
                    value={formData.group_size} 
                    onValueChange={(value) => setFormData({ ...formData, group_size: value })}
                  >
                    <SelectTrigger className="border-gray-200 h-12" data-testid="contact-group-size-select">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1-5">1-5 people</SelectItem>
                      <SelectItem value="6-15">6-15 people</SelectItem>
                      <SelectItem value="16-30">16-30 people</SelectItem>
                      <SelectItem value="30+">30+ people</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="font-heading text-sm font-bold text-navy mb-2 block uppercase tracking-wider">
                  Trip Type
                </label>
                <Select 
                  value={formData.trip_type} 
                  onValueChange={(value) => setFormData({ ...formData, trip_type: value })}
                >
                  <SelectTrigger className="border-gray-200 h-12" data-testid="contact-trip-type-select">
                    <SelectValue placeholder="What are you interested in?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hiking">Hiking</SelectItem>
                    <SelectItem value="skiing">Skiing / Snowboarding</SelectItem>
                    <SelectItem value="wine">Vineyard / Brewery Tour</SelectItem>
                    <SelectItem value="water">Water Sports / Rafting</SelectItem>
                    <SelectItem value="beach">Beach Bus</SelectItem>
                    <SelectItem value="boat">Boat Party / Fishing</SelectItem>
                    <SelectItem value="corporate">Corporate Event</SelectItem>
                    <SelectItem value="custom">Custom / Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="font-heading text-sm font-bold text-navy mb-2 block uppercase tracking-wider">
                  Message
                </label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Tell us about your ideal adventure..."
                  className="border-gray-200 min-h-[120px]"
                  data-testid="contact-message-textarea"
                  required
                />
              </div>

              <Button 
                type="submit" 
                disabled={loading}
                className="w-full btn-primary h-14 flex items-center justify-center gap-2"
                data-testid="contact-submit-btn"
              >
                {loading ? "Sending..." : (
                  <>
                    Send Message <Send size={18} />
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const links = {
    adventures: [
      { name: "Hiking", href: "#activities" },
      { name: "Skiing", href: "#activities" },
      { name: "Wine Tours", href: "#activities" },
      { name: "Beach Bus", href: "#activities" },
      { name: "Boat Parties", href: "#activities" },
    ],
    company: [
      { name: "About Us", href: "#about" },
      { name: "Upcoming Trips", href: "#trips" },
      { name: "Contact", href: "#contact" },
      { name: "Careers", href: "#" },
    ],
  };

  return (
    <footer className="bg-navy-dark text-white py-16 md:py-24 px-6 md:px-12" data-testid="footer">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Logo & Description */}
          <div className="lg:col-span-2">
            <img src={LOGO_URL} alt="Waypoint Group" className="h-14 mb-6" />
            <p className="font-body text-white/70 max-w-md leading-relaxed mb-8">
              Getting city dwellers outdoors since 2020. Your adventure awaits 
              just beyond the skyline.
            </p>
            <div className="flex gap-4">
              <a 
                href="#" 
                className="w-10 h-10 bg-white/10 rounded-sm flex items-center justify-center hover:bg-secondary hover:text-navy transition-colors"
                data-testid="footer-instagram"
              >
                <Instagram size={18} />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 bg-white/10 rounded-sm flex items-center justify-center hover:bg-secondary hover:text-navy transition-colors"
                data-testid="footer-facebook"
              >
                <Facebook size={18} />
              </a>
              <a 
                href="#" 
                className="w-10 h-10 bg-white/10 rounded-sm flex items-center justify-center hover:bg-secondary hover:text-navy transition-colors"
                data-testid="footer-twitter"
              >
                <Twitter size={18} />
              </a>
            </div>
          </div>

          {/* Adventures Links */}
          <div>
            <h4 className="font-heading text-lg font-bold uppercase tracking-wider mb-6">
              Adventures
            </h4>
            <ul className="space-y-3">
              {links.adventures.map((link) => (
                <li key={link.name}>
                  <a 
                    href={link.href} 
                    className="font-body text-white/70 hover:text-secondary transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-heading text-lg font-bold uppercase tracking-wider mb-6">
              Company
            </h4>
            <ul className="space-y-3">
              {links.company.map((link) => (
                <li key={link.name}>
                  <a 
                    href={link.href} 
                    className="font-body text-white/70 hover:text-secondary transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="font-body text-white/50 text-sm">
            © {new Date().getFullYear()} The Waypoint Group. All rights reserved.
          </p>
          <div className="flex gap-6">
            <a href="#" className="font-body text-white/50 text-sm hover:text-white transition-colors">
              Privacy Policy
            </a>
            <a href="#" className="font-body text-white/50 text-sm hover:text-white transition-colors">
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activities, setActivities] = useState([]);
  const [trips, setTrips] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [tripRequestModal, setTripRequestModal] = useState({ isOpen: false, trip: null });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [activitiesRes, tripsRes, testimonialsRes] = await Promise.all([
          axios.get(`${API}/activities`),
          axios.get(`${API}/trips`),
          axios.get(`${API}/testimonials`),
        ]);
        setActivities(activitiesRes.data);
        setTrips(tripsRes.data);
        setTestimonials(testimonialsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  const handleRequestTrip = (trip) => {
    setTripRequestModal({ isOpen: true, trip });
  };

  const handleCloseModal = () => {
    setTripRequestModal({ isOpen: false, trip: null });
  };

  return (
    <div className="min-h-screen" data-testid="app-container">
      <Toaster position="top-right" richColors />
      <Navigation isScrolled={isScrolled} />
      <HeroSection />
      <ActivitiesSection activities={activities} />
      <TripIdeasSection trips={trips} onRequestTrip={handleRequestTrip} />
      <TestimonialsSection testimonials={testimonials} />
      <AboutSection />
      <NewsletterSection />
      <ContactSection />
      <Footer />
      <TripRequestModal 
        isOpen={tripRequestModal.isOpen} 
        onClose={handleCloseModal}
        selectedTrip={tripRequestModal.trip}
      />
    </div>
  );
}

export default App;
